<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Events"
      subtitle="Manage upcoming and past events"
      add-label="Add"
      :show-view="true"
      :show-edit="true"
      :show-delete="true"
      @add="$router.push('/societyadmin/event/add')"
      @view="$router.push('/societyadmin/event/view/' + $event.id)"
      @edit="$router.push('/societyadmin/event/edit/' + $event.id)"
      @delete="confirmDeleteDialog($event.id)"
      :columns="columns"
      :items="events"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
    >
      <!-- Header Extra: Tab Navigation -->
      <template #header-extra>
        <div class="tab-pill-wrap">
          <button
            v-for="tab in event_type"
            :key="tab.id"
            class="tab-pill-btn"
            :class="{ active: activeTabId === tab.id }"
            @click="switchTab(tab.id)"
          >
            <v-icon
              size="16"
              class="mr-2"
              :color="tab.id === activeTabId ? 'var(--btn-color)' : 'var(--btn-color-dark)'"
            >{{
              tab.id === "upcoming" ? "mdi-calendar-star" : "mdi-calendar-check"
            }}</v-icon>
            {{ tab.text }}
          </button>
        </div>
      </template>

      <!-- Title Column -->
      <template #col-title="{ item }">
        <v-tooltip
          bottom
          nudge-bottom="4"
          color="primary"
          max-width="500"
          :disabled="!item.title || item.title.length <= 40"
        >
          <template v-slot:activator="{ on, attrs }">
            <span class="event-title-clamp" v-bind="attrs" v-on="on">{{
              item.title
            }}</span>
          </template>
          <span>{{ item.title }}</span>
        </v-tooltip>
      </template>

      <!-- Date Columns -->
      <template #col-start_datetime="{ item }">
        <div class="event-date-cell">
          <v-icon size="14" color="#64748B" class="mr-1"
            >mdi-calendar-clock</v-icon
          >
          <span>{{ item.start_datetime | formatDateTime }}</span>
        </div>
      </template>

      <template #col-end_datetime="{ item }">
        <div class="event-date-cell">
          <v-icon size="14" color="#94A3B8" class="mr-1">mdi-calendar-clock</v-icon>
          <span class="muted-text">{{ item.end_datetime | formatDateTime }}</span>
        </div>
      </template>
    </PremiumTable>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Cancel / Delete Event"
      message="Are you sure you want to delete this event? This will remove all event details and notifications sent to members."
      confirm-text="Yes, Delete Event"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteEvent"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapMutations, mapState } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "EventList",

  mixins: [filterMixin],

  components: { PremiumTable, ConfirmModal },

  data() {
    return {
      search: "",
      events: [],
      totalRecords: 0,
      loading: false,
      deleteLoading: false,
      deleteDialog: false,
      delete_id: "",
      page: 1,
      itemsPerPage: 10,
      activeTabId: "upcoming",
      event_type: [
        { id: "upcoming", text: "Upcoming" },
        { id: "past", text: "Past" },
      ],
      user: {},
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor"]),

    columns() {
      return [
        {
          key: "title",
          label: this.$t("event.headers.title") || "Event Title",
          width: "300px",
        },
        {
          key: "start_datetime",
          label: this.$t("event.headers.start_datetime") || "Starts At",
          width: "180px",
        },
        {
          key: "end_datetime",
          label: this.$t("event.headers.end_datetime") || "Ends At",
          width: "180px",
        },
      ];
    },
  },

  watch: {
    page() {
      this.getEventTypeData();
    },
    itemsPerPage() {
      this.getEventTypeData();
    },
    search() {
      this.page = 1;
      this.getEventTypeData();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
    },

    debounceSearch: debounce(function () {
      this.getEventTypeData();
    }, 500),

    switchTab(id) {
      this.activeTabId = id;
      this.page = 1;
      this.getEventTypeData();
    },

    getEventTypeData() {
      this.loading = true;
      const type = this.activeTabId;

      this.$axios
        .get(`societyadmin/${type}`, {
          params: {
            society_id: this.user.id,
            page: this.page,
            perPage: this.itemsPerPage,
            sortBy: "created_at",
            sortDesc: true,
            search: this.search,
          },
        })
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.events = data.events;
            this.totalRecords = data.total;
            if (data.page > data.lastPage) this.page = 1;
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    deleteEvent() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/events/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getEventTypeData();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },



    confirmDeleteDialog(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};
    
    // Initialize persistent filters for this module
    this.initFilters('event');

    this.getEventTypeData();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Custom Tab Pills ── */
.tab-pill-wrap {
  display: flex;
  gap: 4px;
  background: var(--surface2, #f8fafc);
  padding: 4px;
  border-radius: 12px;
  border: 1px solid var(--border, #e2e8f0);
}

@media (max-width: 600px) {
  .tab-pill-wrap {
    width: 100%;
  }
  .tab-pill-btn {
    flex: 1;
    justify-content: center;
    padding: 8px 4px;
  }
}
.tab-pill-btn {
  display: flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx3, #6b7280);
  transition: all 0.2s;
  border: none;
  background: transparent;
  cursor: pointer;
}
.tab-pill-btn:hover {
  color: var(--tx2, #374151);
}
.tab-pill-btn.active {
  background: #ffffff;
  color: var(--brand, #0d9972);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* ── Cells ── */
.event-title-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--tx);
  cursor: default;
}
.event-date-cell {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2);
}
.muted-text {
  color: var(--tx3);
  font-weight: 500;
}
</style>
